import languageCodes from './reddit-language-codes.json';

export const youtubeMetricKeys = ['min_video_views', 'min_likes', 'min_comments'] as const;
export const youtubeDurationKeys = ['min_duration_seconds', 'max_duration_seconds'] as const;
export interface MarketYoutubeResultFilters {
    published_from?: string;
    published_to?: string;
    languages?: string[];
    min_video_views?: number;
    min_likes?: number;
    min_comments?: number;
    min_duration_seconds?: number;
    max_duration_seconds?: number;
}

/** Used at authoring and preview boundaries, including explicit clearing on older backends. */
export function normalizeYoutubeResultFilters(input: unknown): MarketYoutubeResultFilters {
    if (input == null) return {};
    if (typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid YouTube filters');
    const filters = input as Record<string, unknown>;
    const keys = new Set<string>(['published_from', 'published_to', 'languages', ...youtubeMetricKeys, ...youtubeDurationKeys]);
    if (Object.keys(filters).some(key => !keys.has(key))) throw new Error('Unsupported YouTube filter');
    const result: MarketYoutubeResultFilters = {};
    for (const key of ['published_from', 'published_to'] as const) if (key in filters) {
        const value = filters[key];
        const date = typeof value === 'string' ? new Date(value + 'T00:00:00Z') : new Date(NaN);
        if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(date.getTime())
            || date.toISOString().slice(0, 10) !== value) throw new Error('Invalid UTC publication date');
        result[key] = value;
    }
    if (result.published_from && result.published_to && result.published_from > result.published_to) throw new Error('Invalid date range');
    if ('languages' in filters) {
        const values = filters.languages;
        if (!Array.isArray(values) || values.length > languageCodes.length || values.some(value => typeof value !== 'string'))
            throw new Error('Invalid language list');
        const normalized = [...new Set(values.map(value => value.trim().toLowerCase()))].sort();
        if (normalized.some(value => !languageCodes.includes(value))) throw new Error('Unsupported language');
        if (normalized.length) result.languages = normalized;
    }
    for (const key of [...youtubeMetricKeys, ...youtubeDurationKeys]) if (key in filters) {
        const value = filters[key];
        if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) throw new Error('Invalid interaction minimum');
        result[key] = value;
    }
    if (result.min_duration_seconds != null && result.max_duration_seconds != null
        && result.min_duration_seconds > result.max_duration_seconds) throw new Error('Invalid duration range');
    return result;
}
